import { supabase } from '../lib/supabase';
import { resolveUsuarioIdForRpc } from '../lib/resolveUsuarioIdForRpc';
import type { FacturaDevolucionItem } from '../types/facturas';

function parseFacturaItems(raw: unknown): Record<string, unknown>[] {
  if (raw == null || raw === '') return [];
  try {
    const v = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(v) ? (v as Record<string, unknown>[]) : [];
  } catch {
    return [];
  }
}

function lineProductId(row: Record<string, unknown>): string | null {
  const a = row.productoId ?? row.producto_id;
  if (a != null && String(a).trim()) return String(a);
  return null;
}

function lineQty(row: Record<string, unknown>): number {
  const n = Number(row.cantidad ?? row.qty ?? 0);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
}

/** Cantidad máxima facturada por producto (suma líneas). */
export function maxQtyByProductFromFacturaItems(items: unknown): Map<string, number> {
  const m = new Map<string, number>();
  for (const row of parseFacturaItems(items)) {
    const pid = lineProductId(row);
    if (!pid) continue;
    m.set(pid, (m.get(pid) || 0) + lineQty(row));
  }
  return m;
}

async function fetchFacturaRow(facturaId: string, empresaId: string) {
  try {
    const { data, error } = await supabase
      .from('facturas')
      .select(
        'id, empresa_id, cliente_id, items, saldo_pendiente, monto_pendiente, notas, numero, estado'
      )
      .eq('id', facturaId)
      .eq('empresa_id', empresaId)
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new Error('Factura no encontrada');
    return data as {
      id: string;
      empresa_id: string;
      cliente_id: string | null;
      items: unknown;
      saldo_pendiente: number | null;
      monto_pendiente: number | null;
      notas: string | null;
      numero: string;
      estado: string;
    };
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Error al cargar la factura';
    throw new Error(message);
  }
}

/**
 * Marca la factura como anulada (el trigger de BD restaura inventario).
 * Registra fila en `factura_devoluciones`.
 */
export async function anularFactura(facturaId: string, empresaId: string): Promise<void> {
  try {
    const usuarioId = await resolveUsuarioIdForRpc();
    const { error } = await supabase.rpc('rpc_anular_factura', {
      p_factura_id: facturaId,
      p_empresa_id: empresaId,
      p_usuario_id: usuarioId,
    });
    if (error) throw error;
  } catch (e: unknown) {
    if (e instanceof Error) throw e;
    throw new Error(String(e));
  }
}

/**
 * Devolución parcial: repone stock y ajusta saldos / notas.
 */
export async function procesarDevolucionParcial(
  facturaId: string,
  itemsDevueltos: FacturaDevolucionItem[],
  empresaId: string
): Promise<void> {
  try {
    const merged = new Map<string, FacturaDevolucionItem>();
    for (const it of itemsDevueltos) {
      if (it.cantidad <= 0) continue;
      const cur = merged.get(it.producto_id);
      if (cur) {
        cur.cantidad += it.cantidad;
      } else {
        merged.set(it.producto_id, { ...it });
      }
    }
    const list = Array.from(merged.values()).filter((x) => x.cantidad > 0);
    if (list.length === 0) throw new Error('Indica al menos una cantidad a devolver');

    const { error } = await supabase.rpc('rpc_devolucion_parcial', {
      p_factura_id: facturaId,
      p_items: list,
      p_empresa_id: empresaId,
    });
    if (error) throw error;
  } catch (e: unknown) {
    if (e instanceof Error) throw e;
    throw new Error(String(e));
  }
}

export async function procesarCambio(
  facturaId: string,
  productoDevueltoId: string,
  cantidadDevuelta: number,
  productoNuevoId: string,
  cantidadNueva: number,
  empresaId: string
): Promise<void> {
  try {
    const { error } = await supabase.rpc('rpc_procesar_cambio', {
      p_factura_id: facturaId,
      p_cambios: {
        producto_devuelto_id: productoDevueltoId,
        cantidad_devuelta: cantidadDevuelta,
        producto_nuevo_id: productoNuevoId,
        cantidad_nueva: cantidadNueva,
      },
      p_empresa_id: empresaId,
    });
    if (error) throw error;
  } catch (e: unknown) {
    if (e instanceof Error) throw e;
    throw new Error(String(e));
  }
}

export async function generarCreditoTienda(
  facturaId: string,
  clienteId: string,
  monto: number,
  empresaId: string
): Promise<void> {
  try {
    if (!clienteId) throw new Error('Cliente requerido');
    if (monto <= 0) throw new Error('El monto debe ser mayor a 0');
    const f = await fetchFacturaRow(facturaId, empresaId);
    if (String(f.estado || '').toUpperCase() === 'ANULADA') {
      throw new Error('No se puede generar crédito sobre factura anulada');
    }

    const { data: cli, error: cErr } = await supabase
      .from('clientes')
      .select('id, credito_tienda')
      .eq('id', clienteId)
      .eq('empresa_id', f.empresa_id)
      .maybeSingle();
    if (cErr) throw cErr;
    if (!cli) throw new Error('Cliente no encontrado');

    const uid = await resolveUsuarioIdForRpc();
    const { data: devRow, error: devErr } = await supabase
      .from('factura_devoluciones')
      .insert({
        empresa_id: f.empresa_id,
        factura_id: facturaId,
        tipo: 'CREDITO_TIENDA',
        credito_generado: monto,
        notas: `Crédito generado: $${Math.round(monto).toLocaleString('es-CO')}`,
        creado_por: uid,
      })
      .select('id')
      .single();
    if (devErr) throw devErr;
    if (!devRow?.id) throw new Error('No se pudo registrar la devolución');

    const { error: cobErr } = await supabase.from('cobros').insert({
      empresa_id: f.empresa_id,
      cliente_id: clienteId,
      factura_id: facturaId,
      tipo: 'CREDITO_TIENDA',
      tipo_cobro: 'CREDITO_TIENDA',
      monto,
      estado: 'DISPONIBLE',
      notas: `Crédito generado por devolución de factura ${f.numero}`,
      factura_devolucion_id: devRow.id,
      usado: false,
    });
    if (cobErr) throw cobErr;

    const actual = Number(cli.credito_tienda) || 0;
    const { error: uCli } = await supabase
      .from('clientes')
      .update({ credito_tienda: actual + monto })
      .eq('id', clienteId)
      .eq('empresa_id', f.empresa_id);
    if (uCli) throw uCli;

    const nota = `\n[Crédito tienda ${new Date().toLocaleString('es-CO')}] +$${Math.round(monto).toLocaleString('es-CO')}`;
    const { error: fuErr } = await supabase
      .from('facturas')
      .update({
        notas: `${f.notas || ''}${nota}`.trim(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', facturaId)
      .eq('empresa_id', f.empresa_id);
    if (fuErr) throw fuErr;
  } catch (e: unknown) {
    if (e instanceof Error) throw e;
    throw new Error(String(e));
  }
}

export function useDevoluciones() {
  return {
    anularFactura,
    procesarDevolucionParcial,
    procesarCambio,
    generarCreditoTienda,
    maxQtyByProductFromFacturaItems,
  };
}
